import { useMemo, useState, useCallback } from 'react';
import styles from './YearlyContentCalendarViewer.module.css';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const formatMonthLabel = (value) => {
  if (!value) return 'Unknown Month';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  } catch {
    return value;
  }
};

const normalizeCalendarEntryDate = (entry) => {
  if (!entry?.date) return null;
  const parsed = new Date(entry.date);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const Chip = ({ text }) => {
  if (!text) return null;
  return (
    <span className={styles.chip}>
      <span className={styles.chipText}>{text}</span>
    </span>
  );
};

const EntryCard = ({ entry }) => {
  if (!entry) return null;

  const title = entry.title || 'Untitled';
  const date = entry.date || 'Unknown date';
  const angle = entry.angle || null;
  const notes = entry.notes || null;

  return (
    <div className={styles.entryCard}>
      <div className={styles.entryAccent} />

      <div className={styles.entryContent}>
        <div className={styles.entryTopRow}>
          <span className={styles.datePill}>
            <span className={styles.datePillText}>{date}</span>
          </span>

          {/* <span className={styles.typeBadge}> */}
            {/* <span className={styles.typeBadgeText}>Calendar</span> */}
          {/* </span> */}
        </div>

        <h5 className={styles.entryTitle}>{title}</h5>

        {(angle || notes) ? (
          <div className={styles.metaRow}>
            {/* {angle ? <Chip text={`Angle: ${angle}`} /> : null} */}
            {/* {notes ? <Chip text={`Notes: ${notes}`} /> : null} */}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const TogglePanel = ({ month, entries }) => {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  return (
    <div className={`${styles.panel} ${open ? styles.panelOpen : ''}`}>
      <div
        className={styles.panelHeader}
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && toggle()}
      >
        <div className={styles.panelHeaderLeft}>
          <h4 className={styles.panelTitle}>{month}</h4>
          <span className={styles.countPill}>
            <span className={styles.countPillText}>{entries.length} entries</span>
          </span>
        </div>

        <div className={styles.panelHeaderRight}>
          <span className={styles.panelToggleIcon}>{open ? '⌃' : '⌄'}</span>
        </div>
      </div>

      {open ? (
        <div className={styles.panelBody}>
          {entries.map((entry, idx) => (
            <EntryCard key={`${entry.date || idx}-${idx}`} entry={entry} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    {children}
  </div>
);

const YearlyContentCalendarViewer = ({ data = {} }) => {
  const payload = data?.data || data;
  const summary =
    typeof payload?.summary === 'string' ? payload.summary.trim() : '';
  const calendar = useMemo(
    () => (Array.isArray(payload.calendar) ? payload.calendar : []),
    [payload.calendar]
  );

  const groupedByMonth = useMemo(() => {
    const map = new Map();
    calendar
      .map((entry) => ({
        ...entry,
        parsedDate: normalizeCalendarEntryDate(entry),
      }))
      .filter((entry) => entry.parsedDate)
      .sort((a, b) => a.parsedDate - b.parsedDate)
      .forEach((entry) => {
        const key = `${entry.parsedDate.getUTCFullYear()}-${entry.parsedDate.getUTCMonth()}`;
        if (!map.has(key)) {
          map.set(key, {
            label: formatMonthLabel(entry.date),
            entries: [],
          });
        }
        map.get(key).entries.push(entry);
      });

    return Array.from(map.values());
  }, [calendar]);

  return (
    <div className={styles.root}>
      <Section title="">
        {summary ? (
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>Report Summary</p>
            <p className={styles.summaryText}>{summary}</p>
          </div>
        ) : null}
        {!groupedByMonth.length ? (
          <p className={styles.emptyText}>No calendar entries available.</p>
        ) : (
          groupedByMonth.map((monthGroup) => (
            <TogglePanel
              key={monthGroup.label}
              month={monthGroup.label}
              entries={monthGroup.entries}
            />
          ))
        )}
      </Section>
    </div>
  );
};

export default YearlyContentCalendarViewer;
